import { useRef, useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { AlertTriangle, ChevronLeft } from 'lucide-react'
import { parseISO, format } from 'date-fns'

import { request } from '@helpers/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import Spinner from '@components/Spinner'
import ShowMore from '@components/ShowMore'
import Tooltip from '@components/Tooltip'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card } from '@/components/ui/card'

const CHAT_MESSAGES_QUERY = gql`
  query chatMessageHistory($id: ID!, $page: Int!) {
    node(id: $id) {
      ... on EducatorProjectSubmission {
        chatMessages(page: $page, perPage: 10, sortDirection: "ASC") {
          pagesCount
          nodesCount
          nodes {
            id
            status
            moderationLabel
            moderationReason
            from
            text
            createdAt
          }
        }
      }
    }
  }
`

const formatModerationLabel = (label) => {
  if (!label) return 'Unspecified'
  return label.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

// Format datetime consistently as HH:mm, dd/MM
const formatChatTimestamp = (date) => {
  if (!date) return ''
  return format(parseISO(date), 'HH:mm, dd/MM')
}

const MessageHistoryModal = ({ id, closeModal }) => {
  const messageThreadRef = useRef(null)
  const { currentMembership: { organization: { id: organizationId, features } } } = useCurrentUser()
  const customModerationEnabled = features.includes('moderation_v2')

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  } = useInfiniteQuery({
    queryKey: ['chatMessages', id],
    queryFn: async ({ pageParam = 1 }) => request(CHAT_MESSAGES_QUERY, { id, page: pageParam }, { 'X-Organization-Id': organizationId }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.node.chatMessages.pagesCount > pages.length) {
        return pages.length + 1
      }

      return false
    }
  })

  useEffect(() => {
    if (status === 'success' && messageThreadRef.current) {
      messageThreadRef.current.scrollTo(0, messageThreadRef.current.scrollHeight)
    }
  }, [status])

  return (
    <Dialog open={true} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Chat history</DialogTitle>
        </DialogHeader>

        <div className='relative flex flex-col h-full mt-5 max-h-[60vh] overflow-y-auto' ref={messageThreadRef}>
          <Choose>
            <When condition={status === 'loading'}>
              <Spinner className='self-center' />
            </When>

            <When condition={data?.pages?.[0]?.node?.chatMessages?.nodesCount === 0}>
              <p>No chat messages sent just yet 😀</p>
            </When>

            <Otherwise>
              <For each='page' of={data.pages} index='index'>
                <div key={`page-${index}`} className='flex flex-col'>
                  <For each='message' of={page.node.chatMessages.nodes}>
                    <div key={message.id} className='flex flex-row mb-2'>
                      <p className='text-sm whitespace-nowrap mr-3 font-semibold text-muted-foreground'>
                        {formatChatTimestamp(message.createdAt)}
                      </p>

                      <div className='text-base'>
                        <p className='whitespace-pre-wrap'>
                          <Choose>
                            <When condition={message.from === 'ASSISTANT'}>
                              <div className='bg-green-100 dark:bg-green-900/30 dark:text-foreground p-2 rounded-md'>
                                <ShowMore content={message.text} />
                              </div>
                            </When>

                            <When condition={message.status === 'MODERATION'}>
                              <div className='flex items-start'>
                                <div className='bg-red-100 dark:bg-red-900/30 dark:text-foreground p-2 rounded-md'>
                                  <ShowMore content={message.text} />
                                </div>
                                <AlertTriangle
                                  id={`moderation-icon-${message.id}`}
                                  className='h-4 w-4 text-yellow-500 ml-1 cursor-pointer flex-shrink-0'
                                />
                                <Tooltip anchorSelect={`#moderation-icon-${message.id}`} place='top'>
                                  <Choose>
                                    <When condition={customModerationEnabled && message.moderationLabel}>
                                      <div className='text-xs p-1'>
                                        <p className='font-semibold'>{formatModerationLabel(message.moderationLabel)}</p>
                                        <If condition={message.moderationReason}>
                                          <p>AI Reason: {message.moderationReason}</p>
                                        </If>
                                      </div>
                                    </When>
                                    <Otherwise>
                                      Unsafe content detected.
                                    </Otherwise>
                                  </Choose>
                                </Tooltip>
                              </div>
                            </When>

                            <Otherwise>
                              <Card className="p-2 border bg-card">
                                <ShowMore content={message.text} />
                              </Card>
                            </Otherwise>
                          </Choose>
                        </p>
                      </div>
                    </div>
                  </For>
                </div>
              </For>

              <Choose>
                <When condition={isFetchingNextPage}>
                  <Spinner className='self-center mb-3' />
                </When>

                <When condition={hasNextPage}>
                  <Button
                    variant='outline'
                    className='self-center'
                    onClick={fetchNextPage}
                  >
                    Load more
                  </Button>
                </When>
              </Choose>
            </Otherwise>
          </Choose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MessageHistoryModal
